import * as S from "./style";
import * as React from "react";
import Logo from "../../../assets/logo-notext.png";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";

import { Tooltip } from "@mui/material";

import GuidelineSources from "../GuidelineSources";
import MemorySources from "../MemorySources";
import { StyledTooltip } from "./SystemPatientResults";
import { GuidelineDocWithChunks } from "../GuidelineSources/types";
import { MemoryFact } from "common/services/stream";

export type Roles = "user" | "system";

export interface ReferenceIds {
  [uuid: string]: number;
}

export interface Guideline {
  text: string;
  source_img: string;
  chunk_id?: string;
  chunk_index?: number;
  page_no?: number;
  short_title?: string;
}

export interface ChatBoxType {
  role: Roles;
  content: string;
  reference_ids: ReferenceIds;
  client_id?: string;
  messageId?: string; // persisted message_id, used as scroll anchor for memory-fact jumps
  guidelines?: Guideline[];
  guidelineDocs?: GuidelineDocWithChunks[];
  guidelineChunkIds?: string[];
  memoryFacts?: MemoryFact[];
  createdAt?: string;
}

interface ChatBoxProps {
  user: Roles;
  align?: string;
  isLoading?: boolean;
  children?: React.ReactNode;
  guidelines?: Guideline[];
  guidelineDocs?: GuidelineDocWithChunks[];
  guidelineChunkIds?: string[];
  memoryFacts?: MemoryFact[];
  domId?: string; // DOM id for scroll-to-message (memory-fact source jump)
  createdAt?: string;
  [others: string]: any;
}

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function formatTime12h(date: Date): string {
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = date.getHours() >= 12 ? "PM" : "AM";
  const hours12 = date.getHours() % 12 || 12;
  return `${hours12}:${minutes} ${ampm}`;
}

// Short form for the visible label: time only if it's today, otherwise date + time.
function formatShortTimestamp(iso?: string): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  if (isSameDay(date, new Date())) {
    return formatTime12h(date);
  }
  return `${date.getDate()} ${MONTHS_SHORT[date.getMonth()]}, ${formatTime12h(date)}`;
}

// Full form shown in the tooltip on hover: always day, month, year and time.
function formatFullTimestamp(iso?: string): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return `${date.getDate()} ${MONTHS_SHORT[date.getMonth()]} ${date.getFullYear()} ${formatTime12h(date)}`;
}

export default function ChatBox(props: ChatBoxProps) {
  const [hovered, setHovered] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const timerRef = React.useRef<number | null>(null);
  // System responses always show the toolbar; user bubbles show on hover only.
  const showActions = props.user === "system" || hovered || copied;

  const shortTimestamp = formatShortTimestamp(props.createdAt);
  const fullTimestamp = formatFullTimestamp(props.createdAt);

  const handleCopy = async () => {
    const text = (contentRef.current?.innerText || "").trim();
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
      }
      timerRef.current = window.setTimeout(() => {
        setCopied(false);
        timerRef.current = null;
      }, 5000); // 5s
    } catch (e) {
      console.error("Copy failed:", e);
    }
  };

  React.useEffect(() => {
    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
      }
    };
  }, []);
  return (
    <S.ChatBoxOuterWrapper
      id={props.domId}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <S.ChatBoxInnerWrapper
        {...props.others}
        sx={{
          justifyContent: props.user === "user" ? "flex-end" : "flex-start",
          // While thinking/streaming, center avatar + indicator on one line.
          // Normal messages keep the original flex-start layout.
          alignItems: props.isLoading ? "center" : "flex-start",
        }}
      >
        {/* AI avatar: original left-column position, pinned to the bottom (dropped onto
            the actions/copy-button row) instead of the top. */}
        <div style={{ alignSelf: props.isLoading ? "center" : "stretch", display: "flex", flexDirection: "column", justifyContent: "flex-end", flexShrink: 0 }}>
          {props.user === "system" ? (
            <img src={Logo} alt="" width="35px" height="35px" style={{ objectFit: "contain", borderRadius: "100%", backgroundColor: "white", padding: "2px", transform: props.isLoading ? "none" : "translateY(30px)" }} />
          ) : null}
        </div>

        <S.ChatBox
          role={props.user}
          id="inner-chatbox"
          /* While thinking/streaming with no text yet, the bubble is transparent so
             the "Pondering…"/"Mulling it over…" indicator isn't shown inside a box. */
          style={props.isLoading ? { backgroundColor: "transparent", padding: 0 } : undefined}
        >
          <div ref={contentRef} className="chatbox-highlight-target">{props.children}</div>
          {!props.isLoading && (
            <S.ActionsBar role={props.user}>
              {props.user === "user" && shortTimestamp && (
                <Tooltip title={fullTimestamp} placement="top">
                  <S.ActionsTimestamp style={{ opacity: hovered ? 1 : 0 }}>
                    {shortTimestamp}
                  </S.ActionsTimestamp>
                </Tooltip>
              )}
              <S.ActionsIconGroup style={{ opacity: showActions ? 1 : 0 }}>
                <StyledTooltip title={copied ? "Copied" : "Copy"} onClick={handleCopy}>
                  {copied ? (
                    <CheckRoundedIcon sx={{ fontSize: 15, color: "success.main" }} />
                  ) : (
                    <ContentCopyIcon sx={{ fontSize: 15, color: "text.secondary" }} />
                  )}
                </StyledTooltip>
                {props.user === "system" && (
                  <React.Fragment>
                    {(props.guidelines && props.guidelines.length > 0) || (props.guidelineDocs && props.guidelineDocs.length > 0) ? (
                      <GuidelineSources
                        guidelines={props.guidelines}
                        guidelineDocs={props.guidelineDocs}
                        guidelineChunkIds={props.guidelineChunkIds}
                      />
                    ) : null}
                    {props.memoryFacts && props.memoryFacts.length > 0 ? (
                      <MemorySources memoryFacts={props.memoryFacts} />
                    ) : null}
                  </React.Fragment>
                )}
              </S.ActionsIconGroup>
              {props.user === "system" && shortTimestamp && (
                <Tooltip title={fullTimestamp} placement="top">
                  <S.ActionsTimestamp style={{ opacity: hovered ? 1 : 0 }}>
                    {shortTimestamp}
                  </S.ActionsTimestamp>
                </Tooltip>
              )}
            </S.ActionsBar>
          )}
        </S.ChatBox>
      </S.ChatBoxInnerWrapper>
    </S.ChatBoxOuterWrapper>
  );
}
